import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import logo from "../../assets/images/logo.png";

import CancellationModal from "../../components/CancellationModal";
import instance from "../../redux/actions/AxiosInstance/authenticated";

const MySubscription = () => {
  const [plansData, setPlansData] = useState([]);
  const [isLoading, setIsLoading] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [planToCancel, setPlanToCancel] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const accessToken = localStorage.getItem("accessToken");

  const features = ["Unlimited Product Scans", "Priority support"];

  const getPlanDetails = async () => {
    try {
      setPageLoading(true);
      const res = await instance.get(
        process.env.REACT_APP_BASE_URL + "/subscription/plans"
      );
      if (res.data.data.length > 0) {
        setPlansData(res.data.data);
      }
    } catch (error) {
      console.log(error);
      toast.error("Failed to load subscription details");
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    if (!accessToken) {
      toast.error("Please log in to view your subscription");
      window.location.href = "/login";
      return;
    }
    getPlanDetails();
  }, [accessToken]);

  const activeSubscription = plansData.find(plan => plan.active === true);

  const handleCancelSubscription = (planId) => {
    setPlanToCancel(planId);
    setShowCancelModal(true);
  };

  if (pageLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <svg
            className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <p className="text-gray-600">Loading your subscription details...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="px-5 py-3.5 flex w-full justify-between items-center font-Inter">
        <div className="flex items-center space-x-3 w-[60%] max-w-[240px]">
          <img src={logo} className="w-3/4 h-auto" alt="ScanProfit Logo" />
        </div>
      </div>

      {/* Content */}
      <div className="min-h-screen">
        {activeSubscription ? (
          <div className="flex justify-center py-8">
            <div className="max-w-2xl w-full mx-4">
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-r from-gray-50 to-blue-50 px-8 py-6 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900">Active Subscription</h2>
                        <p className="text-gray-600">Your current plan is active and running</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-8">
                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Plan Details</h3>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <span className="text-gray-600">Plan Name:</span>
                            <span className="font-medium text-gray-900">{activeSubscription.name || "Current Plan"}</span>
                          </div>
                          <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <span className="text-gray-600">Billing Amount:</span>
                            <span className="font-bold text-2xl text-gray-900">${activeSubscription.renewal_price}</span>
                          </div>
                          <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <span className="text-gray-600">Billing Cycle:</span>
                            <span className="font-medium text-gray-900">
                              {activeSubscription.billing_period === 30 ? 'Monthly' : 
                               activeSubscription.billing_period === 365 ? 'Yearly' : 'Custom'}
                            </span>
                          </div>
                          {activeSubscription.renewal_date && (
                            <div className="flex justify-between items-center py-2 border-b border-gray-100">
                              <span className="text-gray-600">Next Renewal:</span>
                              <span className="font-medium text-gray-900">
                                {new Date(activeSubscription.renewal_date).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </span>
                            </div>
                          )}
                          <div className="flex justify-between items-center py-2">
                            <span className="text-gray-600">Status:</span>
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                              <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                              Active
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Plan Features</h3>
                        <div className="space-y-3">
                          {features.map((feature, index) => (
                            <div key={index} className="flex items-center space-x-3">
                              <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                              </svg>
                              <span className="text-gray-700">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="bg-gray-50 rounded-xl p-6">
                        <h4 className="font-semibold text-gray-900 mb-3">Quick Stats</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Plan Type:</span>
                            <span className="font-medium text-gray-900">{activeSubscription.membership_type}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-gray-100">
                    <div className="flex flex-col sm:flex-row gap-4 justify-end">
                      <div className="flex space-x-4">
                        <button
                          onClick={() => window.location.href = "/dashboard"}
                          className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-200 transition duration-300"
                        >
                          Back to Dashboard
                        </button>
                      </div>
                      <button
                        onClick={() => handleCancelSubscription(activeSubscription.id)}
                        disabled={isLoading}
                        className="bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                      >
                        {isLoading === activeSubscription.id ? (
                          <>
                            <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span>Processing...</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                            </svg>
                            <span>Cancel Subscription</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="text-center max-w-md">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-12 h-12 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                No Active Subscription
              </h2>
              <p className="text-gray-600 mb-8">
                You don't have an active subscription right now. Choose a plan to get started with ScanProfit's powerful features.
              </p>
              <div className="space-y-4">
                <button
                  onClick={() => window.location.href = "/payment-options"}
                  className="w-full bg-gradient-to-r from-green-600 to-green-600 text-white font-semibold py-3 px-6 rounded-lg hover:from-green-700 hover:to-green-700 transition duration-300 shadow-md"
                >
                  View Subscription Plans
                </button>
                <button
                  onClick={() => window.location.href = "/dashboard"}
                  className="w-full bg-gray-100 text-gray-700 font-medium py-3 px-6 rounded-lg hover:bg-gray-200 transition duration-300"
                >
                  Back to Dashboard
                </button>
              </div>
            </div>
          </div>
        )}

      </div>

      <CancellationModal
        showModal={showCancelModal}
        onClose={() => {
          setShowCancelModal(false);
          setPlanToCancel(null);
        }}
        planId={planToCancel}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
      />
    </>
  );
};

export default MySubscription;
